import { ReactNode } from 'react'
import { Link, useLocation } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { Badge } from '../ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { NavCollapsible, NavItem, NavLink, type NavGroup } from './types'
import { useFlowStore } from '@/stores/workflow'
import { useState } from 'react'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { useTranslation } from 'react-i18next'
import { tokens } from '@/locales/translations/tokens'
import { useRouter } from '@tanstack/react-router'
import { ReleaseNotesDropdown } from '@/features/notifications/release-notes'
import { UnreadTicketsBadge } from '@/features/tickets/components/unread-tickets-badge'

export function NavGroup({ title, items }: NavGroup) {
  const { state } = useSidebar()
  const href = useLocation({ select: (location) => location.href })
  const { isRecordingAudio } = useFlowStore()
  const [pendingHref, setPendingHref] = useState<string | null>(null)
  const [openConfirm, setOpenConfirm] = useState(false)
  const { t } = useTranslation()
  const { history } = useRouter()
  const { stopRecordingAndWait, saveFlowToBackend, reloadFlowState } = useFlowStore()
  const [confirmLoading, setConfirmLoading] = useState(false)
  return (
    <SidebarGroup>
      <SidebarGroupLabel>{title}</SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => {
          const key = `${item.title}-${item.url}`

          if (!item.items)
            return (
              <SidebarMenuLink
                key={key}
                item={item}
                href={href}
                onGuardNavigate={(url) => {
                  if (isRecordingAudio) {
                    setPendingHref(url)
                    setOpenConfirm(true)
                    return false
                  }
                  return true
                }}
              />
            )

          if (state === 'collapsed')
            return (
              <SidebarMenuCollapsedDropdown key={key} item={item} href={href} />
            )

          return (
            <SidebarMenuCollapsible
              key={key}
              item={item}
              href={href}
              onGuardNavigate={(url) => {
                if (isRecordingAudio) {
                  setPendingHref(url)
                  setOpenConfirm(true)
                  return false
                }
                return true
              }}
            />
          )
        })}
      </SidebarMenu>
      <ConfirmDialog
        open={openConfirm}
        onOpenChange={setOpenConfirm}
        title={t(tokens.agent.editor.recordAudio.actions.stopRecording)}
        desc={t(tokens.agent.editor.recordAudio.confirmDialog.stopCurrentRecordingDescription)}
        confirmText={t(tokens.ui.actions.stop)}
        destructive={true}
        isLoading={confirmLoading}
        tip={t(tokens.agent.editor.recordAudio.confirmDialog.stopCurrentRecordingTip)}
        handleConfirm={async () => {
          try {
            setConfirmLoading(true)
            if (stopRecordingAndWait) {
              await stopRecordingAndWait()
            }
            if (saveFlowToBackend) {
              await saveFlowToBackend()
            }
            if (reloadFlowState) {
              await reloadFlowState()
            }
            if (pendingHref) {
              history.push(pendingHref)
            }
            setOpenConfirm(false)
            setPendingHref(null)
          } finally {
            setConfirmLoading(false)
          }
        }}
      />
    </SidebarGroup>
  )
}

const NavBadge = ({ children }: { children: ReactNode }) => (
  <Badge className='rounded-full px-1 py-0 text-xs'>{children}</Badge>
)

const SidebarMenuLink = ({ item, href, onGuardNavigate }: { item: NavLink; href: string; onGuardNavigate?: (url: string) => boolean }) => {
  const { setOpenMobile } = useSidebar()
  
  if (item.url === '#release-notes') {
    return (
      <SidebarMenuItem>
        <ReleaseNotesDropdown 
          isActive={checkIsActive(href, item)} 
          icon={item.icon}
        />
      </SidebarMenuItem>
    )
  }

  if (item.url === '/tickets') {
    return (
      <SidebarMenuItem>
        <UnreadTicketsBadge 
          isActive={checkIsActive(href, item)} 
          icon={item.icon}
        />
      </SidebarMenuItem>
    )
  }
  
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={checkIsActive(href, item)}
        tooltip={item.title}
      >
        <Link
            to={item.url as string}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              if (onGuardNavigate && !onGuardNavigate(String(item.url))) {
                e.preventDefault()
                return
              }
              setOpenMobile(false)
            }}
            target={item.redirectionMethod}
        >
          {item.icon && <item.icon />}
          <span>{item.title}</span>
          {item.badge && <NavBadge>{item.badge}</NavBadge>}
          {item.betaFeature && <Badge className='rounded-full px-1 py-0 text-[11px]'>Beta</Badge>}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

const SidebarMenuCollapsible = ({
  item,
  href,
  onGuardNavigate,
}: {
  item: NavCollapsible
  href: string
  onGuardNavigate?: (url: string) => boolean
}) => {
  const { setOpenMobile } = useSidebar()
  return (
    <Collapsible
      asChild
      defaultOpen={checkIsActive(href, item, true)}
      className='group/collapsible'
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton tooltip={item.title}>
            {item.icon && <item.icon />}
            <span>{item.title}</span>
            {item.badge && <NavBadge>{item.badge}</NavBadge>}
            <ChevronRight className='ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90' />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent className='CollapsibleContent'>
          <SidebarMenuSub>
            {item.items.map((subItem) => (
              <SidebarMenuSubItem key={subItem.title}>
                <SidebarMenuSubButton
                  asChild
                  isActive={checkIsActive(href, subItem)}
                >
                   <Link
                     to={subItem.url as string}
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      if (onGuardNavigate && !onGuardNavigate(String(subItem.url))) {
                        e.preventDefault()
                        return
                      }
                      setOpenMobile(false)
                    }}
                    target={subItem.redirectionMethod}
                >
                    {subItem.icon && <subItem.icon />}
                    <span>{subItem.title}</span>
                    {subItem.badge && <NavBadge>{subItem.badge}</NavBadge>}
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}

const SidebarMenuCollapsedDropdown = ({
  item,
  href,
}: {
  item: NavCollapsible
  href: string
}) => {
  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            tooltip={item.title}
            isActive={checkIsActive(href, item)}
          >
            {item.icon && <item.icon />}
            <span>{item.title}</span>
            {item.badge && <NavBadge>{item.badge}</NavBadge>}
            <ChevronRight className='ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90' />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent side='right' align='start' sideOffset={4}>
          <DropdownMenuLabel>
            {item.title} {item.badge ? `(${item.badge})` : ''}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {item.items.map((sub) => (
            <DropdownMenuItem key={`${sub.title}-${sub.url}`} asChild>
              <Link
                to={sub.url}
                className={`${checkIsActive(href, sub) ? 'bg-secondary' : ''}`}
                target={sub.redirectionMethod}
              >
                {sub.icon && <sub.icon />}
                <span className='max-w-52 text-wrap'>{sub.title}</span>
                {sub.badge && (
                  <span className='ml-auto text-xs'>{sub.badge}</span>
                )}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  )
}

function checkIsActive(href: string, item: NavItem, mainNav = false) {
  return (
    href === item.url || // /endpint?search=param
    href.split('?')[0] === item.url || // endpoint
    !!item?.items?.filter((i) => i.url === href).length || // if child nav is active
    (mainNav &&
      href.split('/')[1] !== '' &&
      href.split('/')[1] === item?.url?.split('/')[1])
  )
}
